import { intervalToDuration } from "date-fns"
import ky from "ky"
import { OrganizationRole, WorkspaceRole } from "@/types"

export const getFaviconUrl = (url: string, size?: number) => {
  const resultUrl = `https://www.google.com/s2/favicons?domain=${url}`
  if (size) {
    return `${resultUrl}&sz=${size}`
  }
  return resultUrl
}

export const getAvatarName = (name: string) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
}

export const getHostname = (url: string, fallback: string = "Invalid URL") => {
  try {
    return new URL(url).hostname
  } catch (error) {
    return fallback
  }
}

const avatarColors = [
  "#3498db",
  "#2ecc71",
  "#e74c3c",
  "#f39c12",
  "#9b59b6",
  "#1abc9c",
  "#34495e",
  "#16a085",
  "#e67e22",
  "#95a5a6",
]

export const getAvatarBackgroundColor = (email: string) => {
  const hash = email
    .split("")
    .reduce((acc, char) => char.charCodeAt(0) + acc, 0)
  return avatarColors[hash % avatarColors.length]
}

export const downloadFile = (url: string, overrideFilename?: string) => {
  const filename =
    overrideFilename ||
    url.split("/").pop()?.split("?")[0] ||
    `file-${new Date().toISOString()}`
  ky.get(url)
    .then((response) => response.blob())
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.style.display = "none"
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(blobUrl)
      document.body.removeChild(a)
    })
    .catch((error) => {
      console.error("Download failed:", error)
      alert("Failed to download the file. Please try again later.")
    })
}

export const getResourceUrl = (url: string, root: string) => {
  if (url.startsWith("http")) {
    return url
  }
  return `${root}${url}`
}

export const isDefaultAvatar = (url: string) => {
  return url?.endsWith("CODEER_IMG")
}

export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  delay: number,
  options: { leading?: boolean } = {}
): { (...args: Parameters<T>): void; cancel: () => void } {
  let timerId: NodeJS.Timeout | null = null

  const debouncedFunction = (...args: Parameters<T>) => {
    if (!timerId && options.leading) {
      func(...args)
    }
    if (timerId) {
      clearTimeout(timerId)
    }
    timerId = setTimeout(() => {
      if (!options.leading) {
        func(...args)
      }
      timerId = null
    }, delay)
  }

  debouncedFunction.cancel = () => {
    if (timerId) {
      clearTimeout(timerId)
      timerId = null
    }
  }

  return debouncedFunction
}

export const isOwner = (role: WorkspaceRole | OrganizationRole) => {
  return role === WorkspaceRole.Owner || role === OrganizationRole.Owner
}

export const hasPermission = (
  userPermissions: Set<string>,
  permission: string
) => {
  if (userPermissions.has(permission)) {
    return true
  }

  return false
}

export const shortRelativeTime = (date: Date) => {
  const d = intervalToDuration({ start: date, end: new Date() })

  if (d.years) return `${d.years}y`
  if (d.months) return `${d.months}mo`
  if (d.days) return `${d.days}d`
  if (d.hours) return `${d.hours}h`
  if (d.minutes) return `${d.minutes}m`
  return `${d.seconds || 0}s`
}

export function getWorkspaceIdFromPath(pathname: string): string | undefined {
  try {
    const match = pathname.match(/^\/workspaces\/([^/]+)/)
    return match?.[1]
  } catch {
    return undefined
  }
}
